import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useActivity } from "@/context/ActivityContext";
import BackButton from "@/components/BackButton";
import PilotMonthBadge from "@/components/PilotMonthBadge";
import { Trophy, Timer, Flag, Medal, Share2, Eye, Award, Crown } from "lucide-react";

const medalColor = (pos) =>
  pos === 1 ? "text-yellow-400" : pos === 2 ? "text-zinc-300" : pos === 3 ? "text-amber-600" : "text-zinc-500";

const SESSION_LABEL = { gara: "Gara", prove_libere: "Prove Libere", qualifica: "Qualifica" };

function Stat({ label, value, accent, testid }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900 p-5" data-testid={testid}>
      <div className={`font-display font-black text-3xl ${accent || "text-zinc-50"}`}>{value}</div>
      <div className="mt-1 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">{label}</div>
    </div>
  );
}

export default function PilotDetail() {
  const { champId, name } = useParams();
  const pilotName = decodeURIComponent(name);
  const { isAdmin } = useAuth();
  const { tick } = useActivity();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [showReal, setShowReal] = useState(false);

  useEffect(() => {
    setError(false);
    api.get(`/championships/${champId}/pilots/${encodeURIComponent(pilotName)}`)
      .then((r) => setData(r.data)).catch(() => setError(true));
  }, [champId, pilotName, tick]);

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
        <BackButton className="mb-6" />
        <p className="text-zinc-400" data-testid="pilot-error">Impossibile caricare la scheda pilota.</p>
      </div>
    );
  }
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-20 text-zinc-500">Caricamento…</div>;

  const accent = data.category === "rc" ? "#facc15" : "#06b6d4";
  const t = data.totals || {};

  const sharePilot = async () => {
    const W = 1080, rowH = 60, headH = 320;
    const H = headH + (data.history.length + 1) * rowH + 60;
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#09090b"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = accent; ctx.fillRect(0, 0, W, 10);
    const logo = new Image();
    logo.src = "/team-tuono-logo.png";
    await new Promise((res) => { logo.onload = res; logo.onerror = res; });
    if (logo.complete && logo.naturalWidth) {
      try { ctx.drawImage(logo, W - 150, 38, 104, 104); } catch (e) { /* skip */ }
    }
    ctx.fillStyle = accent; ctx.font = "bold 26px Arial"; ctx.textAlign = "left";
    ctx.fillText("TEAM TUONO", 48, 82);
    ctx.fillStyle = "#fafafa"; ctx.font = "900 60px Arial";
    ctx.fillText(pilotName.toUpperCase().slice(0, 20), 48, 155);
    ctx.fillStyle = "#a1a1aa"; ctx.font = "24px Arial";
    ctx.fillText(data.championship_name || "", 48, 200);
    // stat pills
    const stats = [["PUNTI", t.points || 0], ["GARE", t.races || 0], ["VITTORIE", t.wins || 0], ["PODI", t.podiums || 0]];
    stats.forEach((s, i) => {
      const x = 48 + i * 258;
      ctx.fillStyle = "#16161a"; ctx.fillRect(x, 232, 238, 66);
      ctx.fillStyle = accent; ctx.font = "900 34px Arial"; ctx.textAlign = "left";
      ctx.fillText(String(s[1]), x + 18, 278);
      ctx.fillStyle = "#71717a"; ctx.font = "bold 15px Arial";
      ctx.fillText(s[0], x + 90, 274);
    });
    let y = headH + 20;
    ctx.fillStyle = "#71717a"; ctx.font = "bold 18px Arial"; ctx.textAlign = "left";
    ctx.fillText("EVENTO", 48, y); ctx.fillText("POS", W - 320, y); ctx.fillText("PUNTI", W - 150, y);
    y += 22;
    data.history.forEach((h, i) => {
      ctx.fillStyle = i % 2 ? "#0e0e11" : "#16161a";
      ctx.fillRect(48, y - 30, W - 96, rowH - 8);
      ctx.fillStyle = "#fafafa"; ctx.font = "bold 26px Arial"; ctx.textAlign = "left";
      ctx.fillText((h.event_name || "").slice(0, 26), 66, y);
      ctx.fillStyle = "#d4d4d8"; ctx.font = "bold 26px Arial";
      ctx.fillText("P" + h.final_position, W - 320, y);
      ctx.fillStyle = accent; ctx.font = "900 28px Arial";
      ctx.fillText(String(h.points), W - 150, y);
      y += rowH;
    });
    const blob = await new Promise((res) => c.toBlob(res, "image/png"));
    const file = new File([blob], "pilota.png", { type: "image/png" });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: pilotName, text: `${pilotName} — ${data.championship_name} · Team Tuono` });
        return;
      }
    } catch (e) { /* fallback */ }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `pilota-${pilotName}.png`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Locandina pilota generata");
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14" data-testid="pilot-detail">
      <BackButton className="mb-6" />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="flex items-center gap-1.5 text-xs font-bold tracking-[0.2em] uppercase text-cyan-400">
            <Flag className="h-3.5 w-3.5" /> {data.championship_name}
          </span>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-black tracking-tighter uppercase text-zinc-50" data-testid="pilot-name">
            {pilotName}
          </h1>
          <div className="mt-2"><PilotMonthBadge name={pilotName} /></div>
          {data.pilot_of_month_count > 0 && (
            <div data-testid="pilot-month-count"
              className="mt-3 inline-flex items-center gap-2 border border-yellow-400/40 bg-yellow-400/10 text-yellow-300 text-xs font-bold tracking-[0.12em] uppercase px-3 py-2 rounded-full">
              <Crown className="h-4 w-4 text-yellow-400" />
              {data.pilot_of_month_count} {data.pilot_of_month_count === 1 ? "volta" : "volte"} Pilota del Mese
            </div>
          )}
          {data.race_type && (
            <span data-testid="pilot-race-type" className="mt-2 inline-flex items-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 text-[10px] font-bold tracking-[0.15em] uppercase px-2.5 py-1 rounded-full">
              {data.race_type === "entrambe" ? "RC + SIM" : data.race_type.toUpperCase()}
            </span>
          )}
          {isAdmin && data.real_name && (
            showReal ? (
              <p className="mt-2 text-sm text-yellow-400 font-bold" data-testid="pilot-real-name">
                Nome reale: {data.real_name}
              </p>
            ) : (
              <button onClick={() => setShowReal(true)} data-testid="reveal-real-name-btn"
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 hover:text-yellow-400 transition-colors duration-200">
                <Eye className="h-3.5 w-3.5" /> Mostra nome reale
              </button>
            )
          )}
        </div>
        <button onClick={sharePilot} data-testid="share-pilot-btn"
          className="inline-flex items-center gap-2 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10 text-xs font-bold tracking-[0.15em] uppercase px-4 py-2.5 transition-colors duration-200">
          <Share2 className="h-4 w-4" /> Condividi locandina
        </button>
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Punti" value={t.points || 0} accent="text-yellow-400" testid="pilot-stat-points" />
        <Stat label="Gare" value={t.races || 0} testid="pilot-stat-races" />
        <Stat label="Vittorie" value={t.wins || 0} accent="text-cyan-400" testid="pilot-stat-wins" />
        <Stat label="Podi" value={t.podiums || 0} accent="text-amber-500" testid="pilot-stat-podiums" />
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex items-center gap-3 border border-fuchsia-500/40 bg-fuchsia-500/10 p-5" data-testid="pilot-best-time">
          <Timer className="h-6 w-6 text-fuchsia-300 shrink-0" />
          <div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-fuchsia-300/80">Miglior tempo</div>
            <div className="mt-0.5 font-display font-bold text-lg text-fuchsia-100 tabular-nums">
              {data.best_time || "—"}
              {data.best_time_event && <span className="ml-2 text-xs text-fuchsia-300/70 font-body">· {data.best_time_event}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 border border-zinc-800 bg-zinc-900 p-5" data-testid="pilot-best-position">
          <Award className="h-6 w-6 text-yellow-400 shrink-0" />
          <div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">Miglior posizione</div>
            <div className="mt-0.5 font-display font-bold text-lg text-zinc-50">
              {data.best_position ? `P${data.best_position}` : "—"}
            </div>
          </div>
        </div>
      </div>

      {data.trend && data.trend.length > 0 && (
        <div className="mt-10">
          <h2 className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4">
            <Trophy className="h-4 w-4" /> Andamento punti stagione
          </h2>
          <div className="border border-zinc-800 bg-zinc-900 p-4" data-testid="pilot-trend-chart">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.trend} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="event_name" tick={{ fill: "#71717a", fontSize: 11 }} tickFormatter={(v) => (v || "").slice(0, 10)} />
                <YAxis tick={{ fill: "#71717a", fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#09090b", border: "1px solid #3f3f46", borderRadius: 8, color: "#fafafa" }}
                  labelStyle={{ color: "#a1a1aa" }}
                  formatter={(value) => [value, "Punti totali"]}
                />
                <Line type="monotone" dataKey="cumulative" stroke={accent} strokeWidth={3} dot={{ r: 4, fill: accent }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="mt-10">
        <h2 className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4">
          <Flag className="h-4 w-4" /> Storico gara per gara
        </h2>
        {data.history.length === 0 ? (
          <p className="text-zinc-500 py-8 text-center" data-testid="pilot-empty-history">Nessun risultato registrato.</p>
        ) : (
          <div className="border border-zinc-800" data-testid="pilot-history-table">
            <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-zinc-900 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
              <div className="col-span-5">Gran Premio</div>
              <div className="col-span-2 text-center">Pos</div>
              <div className="col-span-3 text-center">Tempo</div>
              <div className="col-span-2 text-right">Punti</div>
            </div>
            {data.history.map((h, i) => (
              <motion.div key={h.event_id + "-" + i}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                className={`grid grid-cols-12 gap-2 px-5 py-4 items-center border-t border-zinc-800 ${i % 2 ? "bg-transparent" : "bg-zinc-900/50"}`}>
                <div className="col-span-5">
                  <div className="font-heading font-bold text-zinc-50">{h.event_name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{SESSION_LABEL[h.session_type] || "Gara"}{h.date ? ` · ${h.date}` : ""}</div>
                </div>
                <div className={`col-span-2 text-center font-display font-bold text-lg ${medalColor(h.final_position)}`}>
                  {h.final_position <= 3 ? <Medal className={`h-5 w-5 mx-auto ${medalColor(h.final_position)}`} /> : h.final_position}
                </div>
                <div className="col-span-3 text-center font-display text-sm text-cyan-400 tabular-nums">
                  {h.time || <span className="text-zinc-600">—</span>}
                </div>
                <div className="col-span-2 text-right font-display font-bold text-xl text-yellow-400">{h.points}</div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
